import "../ui/styles/challenge.css";
import { ChallengeCodes, ChallengeOutcome, HallOfRecords, dailyRoadFromSave, recordsFromSave } from "@highmarch/app";
import type { Challenge } from "@highmarch/domain";
import { toast } from "../ui/toast";
import { ChallengeTab, showChallengeSheet } from "../ui/modes/challengeSheet";
import { challengeEndHtml } from "../ui/modes/challengeEnd";
import { challengeShareText, fmtClear } from "../ui/modes/challengeWords";
import { canShareCard, shareCard } from "../ui/shareCard";
import { hallOfRecords } from "./records";
import { EndScreenFacts, Feature, HubApi, MORE_ORDER } from "./registry";

/** The row sits between Daily Road (events + 1) and Endless (events + 2) on the Events tab. */
const CHALLENGE_ORDER = MORE_ORDER.events + 1.5;
/** Its block sits where Daily Road's does on the end screen. */
const END_ORDER = 15;
/** Run outcomes kept for end-screen redraws. */
const OUTCOMES_KEPT = 4;
/** The row shows from this many levels before it opens (as Daily Road's does). */
const TEASE_LEVELS = 5;

/**
 * Challenge codes (MP-28, docs/MODES.md §7): the Events tab's row (beside Daily Road), its sheet (enter, create, share,
 * the recent codes with their bests), the end screen's block (the code's best; no rewards), a Guide topic, and debug
 * hooks for captures. Rules live in @highmarch/app challenges.ts; the run is built by the level flow from the fair profile with
 * the code's towers and seed (app runProfile.ts); results go to the Hall of Records (features/records.ts).
 */
export const challengesFeature: Feature = {
  id: "challenges",
  register(ctx, registry) {
    // the Hall the records feature keeps (registered first); a test without it gets a hall of its own
    const hall = hallOfRecords() ?? new HallOfRecords(ctx.catalog, recordsFromSave(ctx.save), () => dailyRoadFromSave(ctx.save));
    const codes = new ChallengeCodes(ctx.catalog, hall);
    const nav = typeof navigator === "undefined" ? null : navigator;
    const start = (c: Challenge) => { const r = codes.run(c); registry.startRun(r.level, r.modifiers); };
    const open = (parent: HTMLElement, tab: ChallengeTab = "enter", code = ""): HTMLElement => showChallengeSheet(parent, {
      catalog: ctx.catalog, codes, now: () => ctx.now(), start,
      share: nav && canShareCard(nav) ? (code, c) => {
        const best = codes.recent().find((r) => r.code === code) ?? null;
        const text = challengeShareText(ctx.balance, code, c, best?.won ? fmtClear(best.ms) : null);
        void shareCard(nav, text, null).then((r) => { if (r === "copied") toast(parent, "Code copied — paste it to a friend"); else if (r === "failed") toast(parent, "Can't share from here"); });
      } : null,
      paste: nav?.clipboard?.readText ? () => nav.clipboard.readText().then((t) => t, () => { toast(parent, "Long-press the box to paste"); return null; }) : null,
    }, tab, code);

    registry.moreRow({
      id: "challenges", order: CHALLENGE_ORDER, icon: "challenge", home: "events",
      label: () => "Challenge codes",
      note: () => {
        if (!codes.open) return `Clear level ${codes.unlockAfterLevel} to unlock`;
        const n = codes.recent().length;
        return n > 0 ? `${n} played` : "Duel a friend";
      },
      visible: () => codes.open || ctx.catalog.highestLevelCleared >= codes.unlockAfterLevel - TEASE_LEVELS,
      open: (hub: HubApi) => { if (codes.open) open(hub.parent); else toast(hub.parent, `Challenge codes open after level ${codes.unlockAfterLevel}`); },
    });

    // The end screen: a run's result goes to the Hall once and is drawn from the cache.
    const outcomes = new Map<string, ChallengeOutcome | null>();
    const outcomeFor = (f: EndScreenFacts): ChallengeOutcome | null => {
      const key = `challenge:${f.sim.seed}:${f.won ? "w" : "l"}${f.sim.stagesCleared}:${f.sim.tick}`;
      if (!outcomes.has(key)) {
        const o = codes.finishRun({ modifiers: f.sim.modifiers, level: f.level, won: f.won, ticks: f.sim.tick, stagesCleared: f.sim.stagesCleared, key, now: ctx.now() });
        outcomes.set(key, o);
        if (outcomes.size > OUTCOMES_KEPT) outcomes.delete(outcomes.keys().next().value!);
        ctx.persist();
      }
      return outcomes.get(key) ?? null;
    };
    registry.endScreenExtra({ id: "challenge", order: END_ORDER, html: (f) => { if (!f.sim.isChallenge) return ""; const o = outcomeFor(f); return o ? challengeEndHtml(o) : ""; } });

    registry.guideTopic({
      id: "challenge", group: "castle", label: "Challenge codes", art: { icon: "flag" }, fromLevel: codes.unlockAfterLevel + 1, steps: ["hub.challenge"],
      lines: [
        "A **challenge code** holds a whole battle: the map, the draw, the towers and up to two twists. Everyone who enters it plays the same fight.",
        "Make one in **Events → Challenge codes** and share it. No rewards: your best goes to the **Hall of Records**.",
      ],
    });

    if (ctx.debug && typeof window !== "undefined") {
      // Captures: open the sheet on a tab (with a code typed), start a code's battle.
      (window as unknown as { __tdChallenge: unknown }).__tdChallenge = {
        codes, open: (tab: ChallengeTab = "enter", code = "") => open(ctx.ui, tab, code),
        start: (code: string) => { const r = codes.read(code); if (r.ok) start(r.challenge); return r; },
      };
    }
  },
};
